import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { MedicineWithOrderDetails } from "types/medicine";

export const MEDICINE_TABLE_HEADERS = [
  { label: "Product Name", key: "name" },
  { label: "SIG", key: "sig" },
  { label: "Dispense", key: "dispense" },
  { label: "Refill", key: "refill" },
  { label: "Actions", key: "actions" },
] as const;

interface MedicineTableProps {
  medicines: MedicineWithOrderDetails[];
  emptyState?: ReactNode;
  loadingState?: ReactNode;
  renderActions?: (item: MedicineWithOrderDetails, index: number) => ReactNode;
  readOnly?: boolean;
  cellPadding?: number;
  sigMaxWidth?: number;
  actionsCellWidth?: string;
  tableContainerProps?: {
    sx?: Record<string, unknown>;
  };
}

function MedicineTable({
  medicines,
  emptyState,
  loadingState,
  renderActions,
  readOnly = false,
  cellPadding = 1.5,
  sigMaxWidth = 250,
  actionsCellWidth,
  tableContainerProps,
}: MedicineTableProps) {
  const { colors } = useCelleraTheme();

  const renderDefaultActions = () => (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        justifyContent: "center",
      }}
    >
      <IconButton
        size="small"
        onClick={() => {}}
        disabled={true}
        sx={{
          color: colors.error[500],
          padding: 0.5,
          "&:hover": {
            backgroundColor: colors.error[100],
          },
          "&:disabled": {
            color: colors.text.secondary,
            opacity: 0.5,
          },
        }}
        title="Delete"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <TableContainer {...tableContainerProps}>
      <Table stickyHeader sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: colors.background.secondary,
            }}
          >
            {MEDICINE_TABLE_HEADERS.map((header) => (
              <TableCell
                key={header.key}
                sx={{
                  fontWeight: 600,
                  py: 1.5,
                  borderRight: `1px solid ${colors.border.main}`,
                  backgroundColor: colors.background.secondary,
                  width:
                    header.key === "actions" ? actionsCellWidth : undefined,
                }}
                align={header.key === "actions" ? "center" : "left"}
              >
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingState ? (
            <>{loadingState}</>
          ) : medicines.length === 0 && emptyState ? (
            <>{emptyState}</>
          ) : medicines.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={MEDICINE_TABLE_HEADERS.length}
                sx={{ border: "none" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      textAlign: "center",
                    }}
                  >
                    No medicines found.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            medicines.map((item, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    backgroundColor: colors.background.secondary,
                  },
                }}
              >
                <TableCell
                  sx={{
                    py: cellPadding,
                    borderRight: `1px solid ${colors.border.main}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, mb: cellPadding === 1.5 ? 0.5 : 0 }}
                  >
                    {item.medicine.brandName || item.medicine.name || "N/A"}
                  </Typography>
                  {item.medicine.packageNdc && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.text.secondary }}
                    >
                      NDC: {item.medicine.packageNdc}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    py: cellPadding,
                    borderRight: `1px solid ${colors.border.main}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: sigMaxWidth,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.orderDetails.sig}
                  >
                    {item.orderDetails.sig}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    py: cellPadding,
                    borderRight: `1px solid ${colors.border.main}`,
                  }}
                >
                  {cellPadding === 1.5 ? (
                    <Typography variant="body2">
                      {item.orderDetails.dispense}
                    </Typography>
                  ) : (
                    item.orderDetails.dispense
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    py: cellPadding,
                    borderRight: `1px solid ${colors.border.main}`,
                  }}
                >
                  {cellPadding === 1.5 ? (
                    <Typography variant="body2">
                      {item.orderDetails.refill}
                    </Typography>
                  ) : (
                    item.orderDetails.refill
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: cellPadding,
                    width: actionsCellWidth,
                  }}
                >
                  {readOnly
                    ? renderDefaultActions()
                    : renderActions
                      ? renderActions(item, index)
                      : null}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default MedicineTable;
